import React from 'react'

import { CommonCardProps } from 'component-library/cards/CardItem.Props'

import { Profile } from 'common/client-data-types/Profile'
import { Skeleton } from 'component-library/content/Skeleton/Skeleton'

import {
    StyledHeadshotWrapper,
    StyledHeadshot,
    StyledByline,
    StyledBylineText,
    StyledBylineTextWithHeadshot,
    StyledBylineName,
    StyledBylinePosition,
    StyledSponsoredLabel,
} from 'component-library/cards/CardByline/CardByline.styled'

import { themedValue } from 'component-library/__styling/themed-value'
import { Product } from 'component-library/__product'
import {
    getImageSizePropsFromType,
    ImageSizeTypes,
} from 'component-library/content/Picture/layouts/helpers/sizes'
import { MaybeLoaded } from 'json-react-layouts-data-loader'
import { useTheme } from '@emotion/react'
import { PublicationCardItem } from 'common/client-data-types/card-types'

export interface Props extends CommonCardProps {
    className?: string
    hasBackground?: boolean
    showProfile?: boolean
    isSponsored?: boolean
    isEdgeToEdgeCard?: boolean
    item: MaybeLoaded<PublicationCardItem>
}

export const CardByline: React.FC<Props> = props => {
    const theme = useTheme()
    const { className, isSponsored, hasBackground, item, showProfile, isEdgeToEdgeCard } = props

    const hasBackgroundColor = themedValue(theme, {
        perthnow: hasBackground,
        fallback: undefined,
    })

    if (!item.loaded) {
        return (
            <StyledByline className={className} hasPadding={false}>
                <Skeleton count={1} height={53} dark={hasBackgroundColor || false} />
            </StyledByline>
        )
    }

    if (showProfile && item.result.profiles && item.result.profiles.length !== 0) {
        return renderBylineWithProfile(
            theme.kind,
            item.result.profiles,
            item.result.byline,
            !!isEdgeToEdgeCard,
            className
        )
    }
    if (showProfile) {
        return (
            <StyledByline className={className} hasPadding={!!isEdgeToEdgeCard}>
                <StyledBylineText>{item.result.byline}</StyledBylineText>
            </StyledByline>
        )
    }

    return (
        <StyledByline className={className} hasPadding={!!isEdgeToEdgeCard}>
            <StyledBylineText>{item.result.byline}</StyledBylineText>
            {isSponsored && <StyledSponsoredLabel />}
        </StyledByline>
    )
}

function getProfileImage(profile: Profile[]) {
    const profileImage = profile.slice(0).map(item => {
        return item.thumbnailPhoto ? item.thumbnailPhoto : undefined
    })

    return `${profileImage}`
}

function getProfileText(profile: Profile[] | undefined, text: string | undefined) {
    if (profile && profile.length !== 0) {
        const profileBlock = profile.map((item, index) => {
            return (
                <React.Fragment key={index}>
                    <StyledBylineName>{item.name}</StyledBylineName>
                    <StyledBylinePosition>{item.position}</StyledBylinePosition>
                </React.Fragment>
            )
        })

        return profileBlock
    }

    return text
}

const getImageWidth = (product: Product) => {
    switch (product) {
        case Product.PerthNow:
            return 106
        case Product.TheWest:
            return 110
        case Product.SevenNews:
            return 126
    }
}

function renderBylineWithProfile(
    product: Product,
    profile: Profile[],
    byline: string,
    isEdgeToEdgeCard: boolean,
    className?: string
) {
    const imageWidth = getImageWidth(product)
    return (
        <StyledByline hasPadding={isEdgeToEdgeCard} className={className}>
            {getProfileImage(profile) && (
                <StyledHeadshotWrapper>
                    <StyledHeadshot
                        image={{
                            altText: byline,
                            crops: {
                                original: {
                                    ratio: { width: 1, height: 1 },
                                    reference: getProfileImage(profile),
                                    width: 53,
                                    height: 53,
                                },
                            },
                        }}
                        layout="object-fit-contain"
                        imageSizes={getImageSizePropsFromType(
                            product,
                            ImageSizeTypes.ProfileHeadshot,
                            imageWidth
                        )}
                        imageWidths={imageWidth}
                    />
                </StyledHeadshotWrapper>
            )}
            <StyledBylineTextWithHeadshot>
                {getProfileText(profile, byline)}
            </StyledBylineTextWithHeadshot>
        </StyledByline>
    )
}
CardByline.displayName = 'CardByline'
